import config from 'config';
import { mapValues, uniq } from 'lodash-es';
import { normalize } from 'normalizr';
import type {
  TagNormalized,
  TagsDictionary,
  TagsEntities,
  TagsState,
} from 'redux/modules/tags.types';
import {
  clearTagDraft,
  createTagDraft,
  deleteTag,
  errorSavingTag,
  receiveTags,
  savedTag,
  tagDeleted,
  updateTagDraft,
  updateTags,
  withXhrStatusReducer,
} from 'redux/modules/tags.types';
import { xhrStatusFactory } from 'redux/reusable/xhrStatus';
import { tagListSchema, tagSchema } from 'schema/tags';
import { reducerWithInitialState } from 'typescript-fsa-reducers';
import { rewriteEmojiPath } from 'utils/emojis';

function unreferenceTagFromParent(tagId: number, parent: TagNormalized): TagNormalized {
  return {
    ...parent,
    children: parent.children.filter(id => id !== tagId),
  };
}

function referenceTagFromParent(tagId: number, parent: TagNormalized): TagNormalized {
  return {
    ...parent,
    children: uniq([...parent.children, tagId]),
  };
}

function deleteTagEntity(tagsDictionary: TagsDictionary, id: number): TagsDictionary {
  const newTagsDictionary = { ...tagsDictionary };
  delete newTagsDictionary[id];
  return newTagsDictionary;
}

function deleteChildTag(
  tagsDictionary: TagsDictionary,
  tagId: number,
  parentTag: TagNormalized
): TagsDictionary {
  return {
    ...tagsDictionary,
    [parentTag.id]: unreferenceTagFromParent(tagId, parentTag),
  };
}

export const normalizeTagList = (raw: object) => {
  return normalize<unknown, TagsEntities, Array<number>>(raw, tagListSchema);
};

export const normalizeTag = (raw: object) => {
  return normalize<unknown, TagsEntities, number>(raw, tagSchema);
};

export const initialTagsState: TagsState = {
  draft: undefined,
  entities: {
    tag: {},
  },
  result: [],
  showEditor: false,
  xhrStatus: xhrStatusFactory(),
};

const tagsReducer = reducerWithInitialState(initialTagsState)
  .caseWithAction(
    receiveTags.done,
    (state, action): TagsState => ({
      ...state,
      entities: {
        ...state.entities,
        tag: mapValues<TagsDictionary, TagNormalized>(action.payload.result.entities.tag, tag =>
          rewriteEmojiPath(tag)
        ),
      },
      result: action.payload.result.result,
      error: undefined,
    })
  )
  .caseWithAction(
    receiveTags.failed,
    (state, action): TagsState => ({ ...state, error: action.payload.error })
  )
  .caseWithAction(
    deleteTag.failed,
    (state, action): TagsState => ({ ...state, error: action.payload.error })
  )
  .case(
    updateTags,
    (state, payload): TagsState => ({
      ...state,
      entities: {
        ...state.entities,
        tag: {
          ...state.entities.tag,
          ...payload.entities.tag,
        },
      },
      result: uniq(state.result.concat(payload.result)),
    })
  )
  .caseWithAction(deleteTag.done, (state, action): TagsState => {
    return deleteTagReducerFunction(state, action.payload.params.tagId);
  })
  .case(tagDeleted, (state, tagId): TagsState => {
    return deleteTagReducerFunction(state, tagId);
  })
  .caseWithAction(
    errorSavingTag,
    (state, action): TagsState => ({
      ...state,
      error: action.payload.error,
    })
  )
  .caseWithAction(savedTag, (state, action): TagsState => {
    const tagEntities = state.entities.tag;
    const tagId = action.payload.result;
    const tag = action.payload.entities.tag[tagId];

    if (!tag.parent_id) {
      // A "top level" tag
      return {
        ...state,
        entities: {
          tag: {
            ...state.entities.tag,
            ...action.payload.entities.tag,
          },
        },
        result: uniq([...state.result, tagId]),
      };
    }

    const parent = tagEntities[tag.parent_id];

    // A "child tag" with a parent tagList
    return {
      ...state,
      entities: {
        tag: {
          ...state.entities.tag,
          ...action.payload.entities.tag,
          [parent.id]: referenceTagFromParent(tag.id, parent),
        },
      },
      result: state.result,
    };
  })
  .caseWithAction(updateTagDraft, (state, action): TagsState => {
    return {
      ...state,
      draft: state.draft
        ? {
            ...state.draft,
            ...action.payload,
          }
        : { ...action.payload },
    };
  })
  .caseWithAction(clearTagDraft, state => {
    return {
      ...state,
      draft: undefined,
    };
  })
  .caseWithAction(createTagDraft, (state, action) => {
    return {
      ...state,
      draft: {
        name: '',
        emoji: `${config.emojiCdnPath}/${config.defaultTagEmoji}`,
        sequence: 0,
        ...(action.payload || {}),
      },
    };
  });

function deleteTagReducerFunction(state: TagsState, tagId: number): TagsState {
  let tagsDictionary = state.entities.tag;
  const tag: TagNormalized | undefined = tagsDictionary[tagId];
  if (!tag) {
    return state;
  }

  if (!tag.parent_id) {
    tag.children.forEach(child => {
      tagsDictionary = deleteTagEntity(tagsDictionary, child);
    });

    return {
      ...state,
      entities: {
        tag: {
          ...deleteTagEntity(tagsDictionary, tagId),
        },
      },
      result: state.result.filter(id => id !== tagId),
    };
  }

  const parentTag = tagsDictionary[tag.parent_id];
  const tagsDictionaryWithoutTag = deleteTagEntity(tagsDictionary, tagId);
  const newTagsDictionary = deleteChildTag(tagsDictionaryWithoutTag, tag.id, parentTag);

  return {
    ...state,
    entities: {
      tag: newTagsDictionary,
    },
  };
}

export default withXhrStatusReducer(tagsReducer);
